// A workspace member's Today (docs/guide.md, docs/sharing.md): three calm
// sections built from what is on her device. Pure so it can be tested; the
// view (views/MemberToday.tsx) only draws it.
import type { TaskResource } from '@/kinds/task/apply'
import { isTemplate } from '@/kinds/task/schema'
import type { ProjectResource } from '@/kinds/project/apply'
import type { Edge } from '@/db/types'
import { ADDED_BY, isFollowing } from '@/lib/follow'
import { dateOf } from '@/lib/time'

/** The project whose chores lead her page: titled Household, else none. */
export const householdOf = (projects: ProjectResource[]): ProjectResource | undefined =>
  projects.find(
    (p) => !p.deleted_at && p.status.state === 'active' && p.spec.title.trim().toLowerCase() === 'household',
  )

/** Where an added task stands, in her words. */
export type AddedStanding = { kind: 'open' } | { kind: 'done' } | { kind: 'moved'; to: string }

export interface AddedTask {
  task: TaskResource
  standing: AddedStanding
}

export interface FollowedProject {
  project: ProjectResource
}

export interface MemberToday {
  /** what is for today: open and due (today or slipped), by time then title */
  chores: TaskResource[]
  /** done today, shown lightly after the open ones */
  doneToday: TaskResource[]
  /** tasks she added: open first, then moved, then done (recent only) */
  added: AddedTask[]
  /** projects the owner keeps her posted on */
  followedProjects: ProjectResource[]
  /** tasks kept posted, on their own or through their project; open first */
  followedTasks: TaskResource[]
  /** a followed task's project when that is why it is followed */
  through: Map<string, ProjectResource>
}

const live = (t: TaskResource) => !t.deleted_at && !isTemplate(t.spec)
const byTime = (a: TaskResource, b: TaskResource) => {
  const ta = a.spec.scheduled_time ?? '99:99'
  const tb = b.spec.scheduled_time ?? '99:99'
  if (ta !== tb) return ta < tb ? -1 : 1
  return a.spec.title.localeCompare(b.spec.title)
}
const doneOn = (t: TaskResource, day: string) =>
  t.status.state === 'done' && !!t.status.completed_at && dateOf(new Date(t.status.completed_at)) === day
const newest = (a: TaskResource, b: TaskResource) => (a.updated_at < b.updated_at ? 1 : -1)

/** How many finished things are kept in view under the open ones. */
const DONE_CAP = 5
const FOLLOW_CAP = 12

export const standingOf = (t: TaskResource, today: string): AddedStanding => {
  if (t.status.state === 'done') return { kind: 'done' }
  if (t.spec.scheduled && t.spec.scheduled > today && (t.status.reschedule_count ?? 0) > 0)
    return { kind: 'moved', to: t.spec.scheduled }
  return { kind: 'open' }
}

export const memberToday = (input: {
  tasks: TaskResource[]
  projects: ProjectResource[]
  edges: Edge[]
  /** her grant id; without it nothing is "hers" and nothing is followed */
  grant: string | null
  today: string
}): MemberToday => {
  const { grant, today } = input
  const tasks = input.tasks.filter(live)
  const projectById = new Map(input.projects.filter((p) => !p.deleted_at).map((p) => [p.id, p]))
  const parent = new Map<string, string>()
  for (const e of input.edges)
    if (e.relation === 'belongs_to' && !e.deleted_at) parent.set(e.from_id, e.to_id)

  const chores = tasks
    .filter((t) => t.status.state === 'next' && !!t.spec.scheduled && t.spec.scheduled <= today)
    .sort((a, b) =>
      a.spec.scheduled! === b.spec.scheduled! ? byTime(a, b) : a.spec.scheduled! < b.spec.scheduled! ? -1 : 1,
    )
  const doneToday = tasks.filter((t) => doneOn(t, today)).sort(byTime)

  const rank = { open: 0, moved: 1, done: 2 } as const
  const addedAll = grant
    ? tasks
        .filter((t) => t.labels[ADDED_BY] === grant && t.status.state !== 'dropped')
        .map((task) => ({ task, standing: standingOf(task, today) }))
    : []
  const open = addedAll
    .filter((a) => a.standing.kind !== 'done')
    .sort((a, b) => rank[a.standing.kind] - rank[b.standing.kind] || newest(a.task, b.task))
  const finished = addedAll
    .filter((a) => a.standing.kind === 'done')
    .sort((a, b) => newest(a.task, b.task))
    .slice(0, DONE_CAP)
  const added = [...open, ...finished]

  const through = new Map<string, ProjectResource>()
  const followedProjects = grant
    ? [...projectById.values()].filter((p) => p.status.state === 'active' && isFollowing(p.labels, grant))
    : []
  const followedAll = grant
    ? tasks.filter((t) => {
        if (t.status.state === 'dropped') return false
        if (isFollowing(t.labels, grant)) return true
        const p = projectById.get(parent.get(t.id) ?? '')
        if (p && isFollowing(p.labels, grant)) {
          through.set(t.id, p)
          return true
        }
        return false
      })
    : []
  const followedTasks = [
    ...followedAll.filter((t) => t.status.state !== 'done').sort(byTime),
    ...followedAll
      .filter((t) => t.status.state === 'done')
      .sort(newest)
      .slice(0, DONE_CAP),
  ].slice(0, FOLLOW_CAP)

  return { chores, doneToday, added, followedProjects, followedTasks, through }
}
